// The live operations page (module 14; module 10 U103 to U106, B800 to B809). The half about NOW:
// what is open at this minute, how long it has run, which clock it has passed, who has been told,
// and what the machines say. History and the client's own record are the company page's.
//
// Five rules bind every line below.
//
//   NOTHING ON THIS PAGE IS YELLOW. Yellow is a decision and this page decides nothing: it watches.
//   The one decision lives in triage.
//
//   RED IS THE BREACHED CLOCK, the "past a clock" plate and the warning about the log, and nothing
//   else. A state is a neutral pill whose word carries it.
//
//   EVERY LINE SAYS WHERE IT CAME FROM. A card states nothing without a source beside it.
//
//   AN EMPTY TABLE IS NOT A ZERO. A figure appears only when a source feeding it has delivered
//   something; otherwise the plate says what has not been read, and names what would fill it.
//   A quiet page must never be mistaken for a quiet floor.
//
//   NOTHING IS COUNTED, RANKED OR CHARTED PER PERSON (W8). Operators appear as the handover report
//   typed them, and nowhere else.
//
// The page sends nothing, raises nothing and notifies nothing (W11, W12). There is not a control on
// it that leaves the browser.
import { ActionButton, CardFrame, Chip, SectionHeading } from '../parts/index.js';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { operations, type Clock, type CompanyMeasures, type IncidentCard, type OperationsView, type Plate, type SourceReading } from './api.js';
import { ago, at, dayOf, span } from './format.js';
// Drawn in the grammar the moved surfaces share (the `up-*` top row, plates, section heads), with
// its own classes beside them, as the Engineering page is. It imports that stylesheet itself rather
// than lean on App.tsx happening to load another page first.
import '../usage/usage.css';
import './liveops.css';

/** B803: the page is about now, so it re-reads every 30 seconds while it is in front, and pauses when it is not. */
const REFRESH_MS = 30_000;

export function LiveOps(): ReactElement {
  const [view, setView] = useState<OperationsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const v = await operations.read();
      if (mine === seq.current) { setView(v); setError(null); setNow(Date.now()); }
    } catch (err) {
      if (mine !== seq.current) return;
      if (err instanceof Unauthenticated) { window.location.href = '/'; return; }
      setError(err instanceof ApiError ? err.message : 'Live operations could not be read.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const tick = () => { if (!document.hidden) void load(); };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [load]);
  // The clocks move between reads, so the run times and countdowns tick on their own rather than
  // sitting still for half a minute on a page whose whole subject is elapsed time.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 10_000); return () => clearInterval(t); }, []);

  if (error !== null) return <div className="up lo"><h1 className="up-title">Live operations</h1><p className="up-said">{error}</p></div>;
  if (view === null) return <div className="up lo"><h1 className="up-title">Live operations</h1><p className="up-said">Reading what is open, and what the machines say…</p></div>;

  const lateness = [view.lateness.machines, view.lateness.floor].filter((x): x is string => x !== null).join(' ');

  return (
    <div className="up lo">
      <div className="up-top">
        <h1 className="up-title">Live operations</h1>
        <span className="up-live"><span className="up-dot" />Read {at(view.readAt)}</span>
      </div>
      <p className="up-note">
        {dayOf(view.day.startsAt)}. The floor’s day runs 08:00 to 08:00 on its own clock, so this is hour {view.day.hour} of it.
        {' '}Cobrain re-reads while this page is in front of you.
      </p>

      {view.leadPlates !== null
        ? <>
          <p className="lo-quiet">{view.leadSays}</p>
          <div className="up-lead">{view.leadPlates.map((p) => <PlateBox key={p.key} p={p} />)}</div>
        </>
        : <div className="up-lead">{view.plates.map((p) => <PlateBox key={p.key} p={p} />)}</div>}

      {view.sportSays !== null && <p className="up-note">{view.sportSays}</p>}
      {lateness !== '' && <p className="up-said">{lateness}</p>}

      <section className="up-sec">
        {view.open.length > 0
          ? <>
            <SectionHeading className="up-sech" title="Open now" />
            <ul className="lo-incs">{view.open.map((c) => <Card key={c.id} c={c} now={now} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} />)}</ul>
          </>
          // "Open now" above "Nothing is open." is two headings for one section: the statement is
          // the heading, as it is on the drawing's quiet day. And when NOTHING has reached the page
          // the lead above has already said so, so the statement goes rather than saying it twice
          // in two wordings; the contract line below is what is left, and it belongs here.
          : view.leadSays === null
            ? <>
              <p className="lo-quiet">{view.openSays}</p>
              <p className="up-said">That is what the sources say, and they are not the whole floor. What Cobrain read, and what it could not read at all, is below.</p>
            </>
            : null}
        {/* Only where there are cards for it to be about: on a page with no incidents it is a
            sentence about incidents, and on the quiet page it answered "Nothing is open." */}
        {view.open.length > 0 && <p className="up-note">{view.contractsWaiting}</p>}
      </section>

      <CompanySection c={view.company} />

      {view.read.length > 0 && (
        <section className="up-sec">
          <SectionHeading className="up-sech" title="What Cobrain has read, and when" />
          <div className="up-bill">
            <ul className="lo-read">{view.read.map((s) => <ReadLine key={s.key} s={s} now={now} />)}</ul>
          </div>
        </section>
      )}

      <section className="up-sec">
        <SectionHeading className="up-sech" title="What this page cannot see" />
        <ul className="lo-cannot">
          {view.cannotSee.map((s) => (
            <li key={s.key}>
              <span><b>{s.name}</b><span className="lo-goes">{s.arrives}</span></span>
              {/* Both states take the NEUTRAL pill: the word carries it. Six "waiting on a change"
                  pills in warn ink put six reds in one list, and red on this page is the breached
                  clock, the plate that counts them and a measured lag — nothing else. Measured on
                  the built page at 1920 in light: it was six of the thirteen reds on the screen. */}
              <Chip className="lo-state off">{s.state === 'no_route' ? 'nothing here will change it' : 'waiting on a change'}</Chip>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * One of the four figures. A plate whose source has delivered nothing does NOT show a nought: it
 * says so in words and names the source it is short of, because a nought and an absence read the
 * same on a screen and mean opposite things about the floor.
 */
/**
 * Frame 5: the company's own measures, and the year against how much sport was on.
 *
 * Every figure is the company's own and none of them is an uptime, which is said out loud rather
 * than left as a gap: the warehouse has no uptime table, so the company's figure lives in its
 * dashboard's own code and a second definition would be a second answer.
 */
function CompanySection({ c }: { c: CompanyMeasures }): ReactElement {
  if (!c.available) {
    return (
      <section className="up-sec">
        <SectionHeading className="up-sech" title="The company’s own measures" />
        <p className="lo-inert">{c.says}</p>
      </section>
    );
  }
  return (
    <section className="up-sec">
      <div className="up-sech">
        <h2>The company’s own measures</h2>
        {c.copiedAt !== null && <p className="up-note">Copied from the warehouse {ago(c.copiedAt, Date.now()) ?? 'just now'}</p>}
      </div>

      <div className="up-lead">{c.plates.map((p) => <PlateBox key={p.key} p={p} />)}</div>
      {c.uptimeSays !== null && <p className="up-note">{c.uptimeSays}</p>}

      {c.behind !== null && (
        <p className="lo-warn">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2 21h20L12 3Z" /><path d="M12 10v5" /><path d="M12 18h.01" /></svg>
          {/* The claim carries the colour and the rest explains it, as the drawing has it: a wholly
              flat warning reads as another note, and a wholly red one shouts at a fact that is not
              anybody's fault. Split on the first sentence rather than shaped in the API, because it
              is how the line is SET, not what it says. */}
          <span><b>{c.behind.split('. ')[0]}.</b>{c.behind.includes('. ') ? ` ${c.behind.slice(c.behind.indexOf('. ') + 2)}` : ''}</span>
        </p>
      )}

      {c.months !== null && c.months.length > 0 && (
        <div className="up-bill">
          <h3 className="up-sech">The year, against how much sport was on</h3>
          <div className="lo-months">
            {c.months.map((m) => (
              <div key={m.month} className={m.recorded ? 'lo-month' : 'lo-month lo-unrecorded'}>
                <div className="lo-bars">
                  <i style={{ height: `${m.incidentsHeight}%` }} />
                  {m.hoursHeight !== null && <i className="sport" style={{ height: `${m.hoursHeight}%` }} />}
                </div>
                <small>{m.label}</small>
                <small>{m.recorded ? m.incidents.toLocaleString('en-GB') : `${m.incidents.toLocaleString('en-GB')}, no hours`}</small>
              </div>
            ))}
          </div>
          <p className="lo-key">
            <span><i />incidents</span>
            <span><i className="sport" />hours of sport ingested</span>
          </p>
          {c.monthsSays !== null && <p className="up-said">{c.monthsSays}</p>}
        </div>
      )}

      {c.sources !== null && c.sources.length > 0 && (
        <div className="up-bill">
          <h3 className="up-sech">Where the year’s incidents came from</h3>
          <p className="up-note">The company’s own labels, as a person entered them.</p>
          <div className="up-scroll">
            <table className="up-table">
              <thead><tr><th scope="col">Source</th><th scope="col">Incidents</th><th scope="col">Share</th></tr></thead>
              <tbody>
                {c.sources.map((x) => (
                  <tr key={x.label}>
                    <th scope="row">{x.label}</th>
                    <td className="up-num">{x.n.toLocaleString('en-GB')}</td>
                    <td>{x.share}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {c.total !== null && <p className="up-note">{c.total}</p>}
        </div>
      )}
    </section>
  );
}

function PlateBox({ p }: { p: Plate }): ReactElement {
  if (p.figure === null) {
    return (
      <div className="up-plate lo-plate-unread">
        <b className="lo-unread">Nothing read yet</b>
        <span>{p.label}</span>
        <small>{p.short !== null ? `${p.short} has delivered nothing` : p.under}</small>
      </div>
    );
  }
  return (
    <div className={p.warn ? 'up-plate up-plate-warn' : 'up-plate'}>
      <b>{p.figure}</b><span>{p.label}</span><small>{p.under}</small>
    </div>
  );
}

/** A source that has delivered: what, and when it last did. A lag of a day or more is warn. */
function ReadLine({ s, now }: { s: SourceReading; now: number }): ReactElement {
  return (
    <li>
      <b>{s.name}</b>
      <span>{s.said}</span>
      <span className={`lo-when${s.state === 'quiet' ? ' late' : ''}`}>{ago(s.lastAt, now) ?? 'nothing yet'}</span>
    </li>
  );
}

const CLOCK_CLASS: Record<Clock['state'], string> = { waiting: 'lo-clock', passed: 'lo-clock past', met: 'lo-clock done', none: 'lo-clock none' };

/**
 * One open incident (U103). The card carries its clocks; it is not a row of the floor's own table.
 *
 * The severity NUMBER is never on it: the scale runs opposite ways between contract families, so
 * the figure alone cannot be read correctly. The card says what it MEANS.
 *
 * A card the floor has not logged (U106) carries "Not logged by the floor" where the severity would
 * be, "No clock started" where the clocks would be, and says in words that Cobrain raised it from
 * the machines alone, so the floor may not have seen it.
 */
function Card({ c, now, open, onToggle }: { c: IncidentCard; now: number; open: boolean; onToggle: () => void }): ReactElement {
  const meta = [c.section, c.supplier, c.issueSource].filter((x): x is string => x !== null && x !== '');
  return (
    <li><CardFrame className={`lo-inc${c.breached ? ' breach' : ''}${c.loggedByFloor ? '' : ' unseen'}`}>
      <p className="lo-inc-meta">
        <Chip className={c.severityProvisional ? 'lo-sev prov' : 'lo-sev'}>{c.severityWords}</Chip>
        {meta.map((m) => <span key={m}>{m}</span>)}
        {/* W8: the initials as the report typed them. Never counted, never ranked, never a link. */}
        {c.operators.length > 0 && <span>Operator {c.operators.join(', ')}</span>}
        <span className="lo-run">{span(c.runMinutes)}</span>
      </p>

      <h3>
        <ActionButton reading type="button" className="lo-open-btn" aria-expanded={open} onClick={onToggle}>
          {c.headline}
          <Chip className="lo-open-mark" aria-hidden="true">{open ? 'Close' : 'What has been said'}</Chip>
        </ActionButton>
      </h3>
      <p className="lo-gist">{c.gist}</p>

      <p className="lo-client">
        {c.client.text ?? 'No client named yet'}
        {c.client.readFrom !== null && <span className="lo-code"> from {c.client.readFrom}</span>}
      </p>

      <div className="lo-clocks">
        {c.loggedByFloor
          ? c.clocks.map((k) => (
            <span key={k.key} className={CLOCK_CLASS[k.state]} title={k.from ?? undefined}>
              {k.dot && <span className="lo-dot" />}
              {k.key === 'contract' && k.state !== 'none' ? <><b>Contract</b>{' '}</> : null}{k.words}
            </span>
          ))
          : <span className="lo-clock none">No clock started: nobody has logged it</span>}
      </div>

      <p className="lo-who">
        {c.loggedByFloor
          ? c.told !== null ? <><b>Told:</b> {c.told}</> : <><b>Told:</b> <span className="lo-none-yet">nobody yet.</span></>
          : <><b>No handover row yet.</b> Cobrain raised this from the machines alone, so the floor may not have seen it.</>}
      </p>

      <p className="lo-from">
        <b>Read from</b>
        {c.readFrom.map((r) => <span key={`${r.words}-${r.at}`}>{r.words}</span>)}
      </p>

      {open && (
        <div className="lo-open">
          <div className="lo-cols">
            <div>
              <h4 className="lo-h4">What has been said, and by whom</h4>
              {c.evidence.length > 0
                ? <ul className="lo-ev">
                  {c.evidence.map((e) => (
                    <li key={e.id}>
                      <time>{at(e.at)}</time>
                      <span>
                        <b>{e.words}</b>
                        <span className="lo-src">{e.source}</span>
                        {e.quote !== null && <span className="lo-quote">“{e.quote}”</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                : <p className="up-note">Nothing has been said about it anywhere Cobrain can read.</p>}
              <p className="up-note">{c.stillOpenBecause}</p>
            </div>
            <div>
              <h4 className="lo-h4">What it is against</h4>
              <dl className="lo-kv">
                <dt>Client</dt><dd>{c.client.text ?? 'Not named by any source'}</dd>
                <dt>Read from</dt><dd>{c.client.readFrom ?? 'nothing'}</dd>
                <dt>Section</dt><dd>{c.section}</dd>
                {c.supplier !== null && <><dt>Supplier</dt><dd>{c.supplier}</dd></>}
                <dt>Ran for</dt><dd>{span(c.runMinutes)}</dd>
              </dl>
              <p className="up-note">No service-level clock is confirmed for this client, so the clocks above are the floor’s own.</p>
            </div>
          </div>
        </div>
      )}
    </CardFrame></li>
  );
}
